/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Typography, Table, Switch, Space, Tag, Button, message, App } from 'antd';
import { DollarOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Product as ProductType } from '@wbstats/shared';
import type { RootState } from '../store';
import { PageLayout } from '../components/PageLayout';
import { useProductOperations } from '../hooks';
import { createApiUrl } from '../config/api';

const { Title, Text } = Typography;

interface ProductToggleState {
  [key: number]: boolean;
}

// Extended interface for table rows that includes sub-rows
interface TableRowData extends ProductType {
  isChild?: boolean;
  parentProductId?: number;
  sizeData?: {
    sizeID: number;
    price: number;
    discountedPrice: number;
    clubDiscountedPrice: number;
    techSizeName: string;
  };
}

export const Prices: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const { currentUser, isLoading: userLoading } = useSelector((state: RootState) => state.user);
  const token = localStorage.getItem('token');

  // Local state for managing toggle states
  const [toggleStates, setToggleStates] = useState<ProductToggleState>({});
  const [isUpdatingPrices, setIsUpdatingPrices] = useState(false);

  const { products, isLoading, fetchProducts } = useProductOperations(token || undefined);

  // Fetch products when user has API token
  useEffect(() => {
    if (currentUser?.wb_api_token) {
      void fetchProducts();
    }
  }, [fetchProducts, currentUser?.wb_api_token]);

  // Initialize toggle states when products are loaded
  useEffect(() => {
    if (products.length > 0) {
      const initialStates: ProductToggleState = {};
      products.forEach((product) => {
        // Initialize toggles from actual enableDiscountDetection field
        initialStates[product.productId] = product.enableDiscountDetection || false;
      });
      setToggleStates(initialStates);
    }
  }, [products]);

  // Handle toggle change with database save
  const handleToggleChange = async (productId: number, checked: boolean): Promise<void> => {
    if (!token) {
      void messageApi.error('Не авторизован');
      return;
    }

    try {
      // Optimistically update the UI
      setToggleStates((prev) => ({
        ...prev,
        [productId]: checked,
      }));

      // Save to database
      const response = await fetch(createApiUrl(`products/${productId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enableDiscountDetection: checked }),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string };
        throw new Error(errorData.error || 'Ошибка при сохранении настроек');
      }

      // Update the product data to keep it in sync
      await fetchProducts();

      void messageApi.success(
        `Автоценообразование ${checked ? 'включено' : 'отключено'} для товара ${productId}`
      );
    } catch (error) {
      console.error('Error saving toggle state:', error);
      void messageApi.error(
        `Ошибка при сохранении: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`
      );

      // Revert the optimistic update on error
      setToggleStates((prev) => ({
        ...prev,
        [productId]: !checked,
      }));
    }
  };

  // Handle master toggle change with database save
  const handleMasterToggle = async (checked: boolean): Promise<void> => {
    if (!token || products.length === 0) {
      void messageApi.error('Нет данных для обновления');
      return;
    }

    try {
      // Optimistically update the UI
      const newStates: ProductToggleState = {};
      products.forEach((product) => {
        newStates[product.productId] = checked;
      });
      setToggleStates(newStates);

      // Save all changes to database
      const promises = products.map((product) =>
        fetch(createApiUrl(`products/${product.productId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ enableDiscountDetection: checked }),
        })
      );

      const responses = await Promise.all(promises);
      const failedRequests = responses.filter((response) => !response.ok);

      if (failedRequests.length > 0) {
        throw new Error(`Не удалось обновить ${failedRequests.length} товаров`);
      }

      // Update the product data to keep it in sync
      await fetchProducts();

      void messageApi.success(
        `Автоценообразование ${checked ? 'включено' : 'отключено'} для всех товаров`
      );
    } catch (error) {
      console.error('Error saving master toggle state:', error);
      void messageApi.error(
        `Ошибка при массовом обновлении: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`
      );

      // Revert to actual product values on error
      const revertedStates: ProductToggleState = {};
      products.forEach((product) => {
        revertedStates[product.productId] = product.enableDiscountDetection || false;
      });
      setToggleStates(revertedStates);
    }
  };

  // Handle pricing data update
  const handleUpdatePricingData = async (): Promise<void> => {
    if (!token || !currentUser?.wb_api_token || products.length === 0) {
      void messageApi.error('Нет данных для обновления или отсутствует API токен');
      return;
    }

    setIsUpdatingPrices(true);

    try {
      // Extract product IDs from current products
      const nmIds = products.map((product) => product.productId);

      const response = await fetch(createApiUrl('products/pricing/batch'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ nmIds }),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string };
        throw new Error(errorData.error || 'Ошибка при обновлении данных');
      }

      const result = (await response.json()) as {
        success: boolean;
        summary: {
          total: number;
          found: number;
          notFound: number;
          updated: number;
          skipped: number;
          skippedDueToDiscountIncrease: number;
          discountAlertsCount: number;
        };
        discountAlerts?: Array<{
          productId: number;
          vendorCode: string;
          oldDiscount: number;
          newDiscount: number;
          discountIncrease: number;
          timestamp: string;
        }>;
        message: string;
      };

      if (result.success) {
        // Show success message with statistics
        const {
          total,
          found,
          notFound,
          updated,
          skipped,
          skippedDueToDiscountIncrease,
          discountAlertsCount,
        } = result.summary;

        if (discountAlertsCount > 0) {
          void messageApi.warning(
            `Данные обновлены! Найдено: ${found}/${total}, Обновлено в БД: ${updated}, Пропущено: ${skipped + notFound}, Пропущено из-за увеличения скидок: ${skippedDueToDiscountIncrease}. ⚠️ ВНИМАНИЕ: Обнаружено ${discountAlertsCount} увеличений скидок - обновления БД заблокированы!`
          );

          // Log discount alerts to console for detailed information
          if (result.discountAlerts) {
            console.group('🔥 Обнаружены увеличения скидок (обновления БД заблокированы):');
            result.discountAlerts.forEach((alert) => {
              console.log(
                `Товар ${alert.productId} (${alert.vendorCode}): ${alert.oldDiscount}% → ${alert.newDiscount}% (+${alert.discountIncrease}%) - ЗАБЛОКИРОВАНО`
              );
            });
            console.groupEnd();
          }
        } else {
          void messageApi.success(
            `Данные обновлены! Найдено: ${found}/${total}, Обновлено в БД: ${updated}, Пропущено: ${skipped + notFound}`
          );
        }

        // Refresh the products list to show updated data
        void fetchProducts();
      } else {
        throw new Error('Неожиданный ответ от сервера');
      }
    } catch (error) {
      console.error('Error updating pricing data:', error);

      if (error instanceof Error) {
        if (error.message.includes('Rate limit')) {
          void messageApi.error(
            `Превышен лимит запросов к API Wildberries. ${error.message}. Попробуйте снова через несколько секунд.`
          );
        } else {
          void messageApi.error(`Ошибка при обновлении данных: ${error.message}`);
        }
      } else {
        void messageApi.error('Неизвестная ошибка при обновлении данных');
      }
    } finally {
      setIsUpdatingPrices(false);
    }
  };

  // Transform products data for table with sub-rows
  const transformProductsForTable = (products: ProductType[]): TableRowData[] => {
    const result: TableRowData[] = [];

    products.forEach((product) => {
      const hasMultipleSizes = product.sizes && product.sizes.length > 1;

      if (hasMultipleSizes) {
        // Add parent row
        result.push({
          ...product,
          sizeData: undefined, // Parent row doesn't show individual size data
        });

        // Add child rows for each size
        product.sizes!.forEach((size, index) => {
          result.push({
            ...product,
            productId: product.productId * 1000 + index + 1, // Unique key for child rows
            name: size.techSizeName,
            isChild: true,
            parentProductId: product.productId,
            sizeData: size,
          });
        });
      } else {
        // Single size product - add size data directly
        result.push({
          ...product,
          sizeData: product.sizes?.[0],
        });
      }
    });

    return result;
  };

  // Calculate discount percentage
  const calculateDiscountPercentage = (price: number, discountedPrice: number): number => {
    if (price <= 0 || discountedPrice >= price) return 0;
    return Math.round(((price - discountedPrice) / price) * 100);
  };

  // Table columns configuration
  const columns: ColumnsType<TableRowData> = [
    {
      title: '№',
      key: 'index',
      width: 60,
      render: (_, record, index) => {
        if (record.isChild) return null;
        // Calculate actual product number by counting only parent rows before this index
        const transformedData = transformProductsForTable(products);
        const parentRowsBeforeThis = transformedData
          .slice(0, index)
          .filter((row) => !row.isChild).length;
        return parentRowsBeforeThis + 1;
      },
      align: 'center' as const,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => {
        if (a.isChild && b.isChild) return a.name.localeCompare(b.name);
        if (a.isChild) return 1;
        if (b.isChild) return -1;
        return a.name.localeCompare(b.name);
      },
      ellipsis: true,
      render: (text: string, record) => {
        if (record.isChild) {
          return (
            <span
              style={{
                paddingLeft: '30px',
                color: '#666',
                fontStyle: 'italic',
                position: 'relative',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: '10px',
                  color: '#ccc',
                }}
              >
                ↳
              </span>
              Размер: {text}
            </span>
          );
        }
        return text;
      },
    },
    {
      title: 'Цена',
      key: 'price',
      width: 120,
      align: 'center' as const,
      render: (_, record) => {
        if (record.sizeData?.price) {
          return `${record.sizeData.price.toLocaleString()} ₽`;
        }
        return record.isChild ? '' : '-';
      },
    },
    {
      title: 'Скидка',
      key: 'discount',
      width: 100,
      align: 'center' as const,
      render: (_, record) => {
        if (record.isChild) return '';

        if (record.sizeData?.price && record.sizeData?.discountedPrice) {
          const discountPercentage = calculateDiscountPercentage(
            record.sizeData.price,
            record.sizeData.discountedPrice
          );
          return discountPercentage > 0 ? `${discountPercentage}%` : '';
        }

        if (record.discount !== undefined && record.discount !== null) {
          return `${record.discount}%`;
        }
        return '';
      },
    },
    {
      title: 'Цена со скидкой',
      key: 'discountedPrice',
      width: 150,
      align: 'center' as const,
      render: (_, record) => {
        if (record.sizeData?.discountedPrice) {
          return `${record.sizeData.discountedPrice.toLocaleString()} ₽`;
        }
        return record.isChild ? '' : '-';
      },
    },
    {
      title: 'Статус автоценообразования',
      key: 'pricing_status',
      width: 200,
      render: (_, record) => {
        if (record.isChild) return null;

        const isEnabled = toggleStates[record.productId] || false;
        return (
          <Space>
            <Switch
              checked={isEnabled}
              onChange={(checked) => void handleToggleChange(record.productId, checked)}
              checkedChildren="Вкл"
              unCheckedChildren="Выкл"
              style={{
                backgroundColor: isEnabled ? '#52c41a' : '#ff4d4f',
              }}
            />
            <Tag color={isEnabled ? 'success' : 'error'}>
              {isEnabled ? 'Включено' : 'Отключено'}
            </Tag>
          </Space>
        );
      },
    },
  ];

  if (!currentUser) return null;

  // Calculate statistics and master toggle state based on current toggle states for UI consistency
  const currentToggleValues = Object.values(toggleStates);
  const enabledCount = currentToggleValues.filter(Boolean).length;
  const disabledCount = currentToggleValues.length - enabledCount;
  const allEnabled =
    products.length > 0 &&
    currentToggleValues.length === products.length &&
    currentToggleValues.every(Boolean);

  return (
    <PageLayout title="Управление ценами на товары">
      {userLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          Загрузка...
        </div>
      ) : currentUser?.wb_api_token ? (
        <>
          {/* Statistics Card */}
          <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space size="large">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                    {products.length}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Всего товаров</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                    {enabledCount}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Автоценообразование вкл</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                    {disabledCount}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Автоценообразование выкл</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <Switch
                      checked={allEnabled}
                      onChange={(checked) => void handleMasterToggle(checked)}
                      checkedChildren="Все вкл"
                      unCheckedChildren="Все выкл"
                      disabled={products.length === 0}
                      style={{
                        backgroundColor: allEnabled ? '#52c41a' : '#ff4d4f',
                      }}
                    />
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Управление всеми</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    loading={isUpdatingPrices}
                    onClick={() => void handleUpdatePricingData()}
                    disabled={products.length === 0 || isLoading}
                    style={{ marginBottom: '8px' }}
                  >
                    Обновить данные
                  </Button>
                  <div style={{ color: '#666', fontSize: '14px' }}>Обновить цены из WB</div>
                </div>
              </Space>
              <DollarOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
            </div>
          </Card>

          {/* Products Table */}
          <Card
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Товары с настройками автоценообразования ({products.length})
              </Title>
            }
          >
            <Table
              dataSource={transformProductsForTable(products)}
              columns={columns}
              loading={isLoading}
              rowKey={(record) =>
                record.isChild ? `${record.parentProductId}-${record.productId}` : record.productId
              }
              style={{ width: '100%' }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} товаров`,
              }}
              scroll={{ x: 'max-content' }}
              size="middle"
            />
          </Card>
        </>
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Title level={3}>Настройка API токена</Title>
            <Text type="secondary">
              Для управления ценами на товары необходимо настроить API токен Wildberries в разделе{' '}
              &ldquo;Настройки&rdquo;.
            </Text>
          </div>
        </Card>
      )}
    </PageLayout>
  );
};
